import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Menu as HUIMenu, Transition } from '@headlessui/react';
import * as Crud from 'src/@crud';
import { useChangeTrackerHelper } from 'lib/hooks/use-change-tracker';
import Button from 'lib/components/button.jsx';
import { Spinner as SpinnerComponent } from 'lib/components/loading.jsx';
import Dialog from 'lib/components/dialog.jsx';
import { getPopInProps } from 'lib/helpers/transition.js';
import { isNotFalse } from 'lib/utils/boolean.js';
import { hasProperties } from 'lib/utils/object.js';
import { addClasses, cn } from 'lib/helpers/class.js';
import { callAll, isFunction } from 'lib/utils/function.js';
import { Icon } from '@iconify-icon/react';
import { Link } from 'react-router-dom';

const Spinner = () => <SpinnerComponent className="mr-1 h-4 w-4 translate-y-0.5" width={2} />;

const getClass = (isMobile, destructive) => {
    if (!isMobile) return undefined;
    return cn(
        destructive ? 'text-red-700 hover:text-red-600' : 'text-inherit hover:text-primary-700',
        'flex text-sm font-semibold text-neutral-700'
    );
};

export function ControlButton(props) {
    const { destructive } = props;
    const isMobile = useIsMobile();
    const className = React.useMemo(() => getClass(isMobile, destructive), [isMobile, destructive]);
    return <Button size="xs" className={className} {...props} />;
}

function GoToButton(props) {
    const { index, to, children, icon, text, ...rest } = props;

    return (
        <Link to={to}>
            <ControlButton {...rest}>
                {icon && <Icon icon={icon} width="1.3rem" height="1.3rem" className="mr-1" />}
                {text || children}
            </ControlButton>
        </Link>
    );
}

function AddButton(props) {
    const isMobile = useIsMobile();
    const className = React.useMemo(() => getClass(isMobile), [isMobile]);
    const { routes } = Crud.useRouteParams();
    const [addRoute] = routes.create.split('/');
    const { to = `../${addRoute}`, ...rest } = props;

    return (
        <ControlButton to={to} className={className} {...rest}>
            <Icon icon="material-symbols:add" width="1.3rem" height="1.3rem" className="mr-1" />
            {_t.global.new()}
        </ControlButton>
    );
}

function SearchButton(props) {
    const { routes } = Crud.useRouteParams();
    const { to = `../${routes.search}`, ...rest } = props;

    return (
        <ControlButton to={to} {...rest}>
            <Icon icon="material-symbols:search-rounded" width="1.3rem" height="1.3rem" className="mr-1" />
            {_t.global.search()}
        </ControlButton>
    );
}

export function SaveButton(props) {
    const { busy, form, showDidSave, ...rest } = props;

    return (
        <ControlButton busy={busy} loadAnimation={false} type="submit" form={form} {...rest}>
            {busy && <Spinner />}
            {!busy && (
                <React.Fragment>
                    {showDidSave ? (
                        <CheckIcon className="icon-sm mr-1" />
                    ) : (
                        <Icon
                            icon="material-symbols:save-outline-rounded"
                            width="1.3rem"
                            height="1.3rem"
                            className="mr-1"
                        />
                    )}
                </React.Fragment>
            )}
            {_t.global.save()}
        </ControlButton>
    );
}

function CloneButton(props) {
    const { busy, ...rest } = props;
    return (
        <ControlButton busy={busy} loadAnimation={false} {...rest}>
            {busy && <Spinner />}
            {!busy && (
                <Icon
                    icon="material-symbols:content-copy-outline-rounded"
                    width="1.3rem"
                    height="1.3rem"
                    className="mr-1"
                />
            )}
            {_t.global.clone()}
        </ControlButton>
    );
}

function DeleteButton(props) {
    const { onDelete, busy, onClick, ...rest } = props;
    const [isOpen, setIsOpen] = useState(false);
    const onClose = () => {
        setIsOpen(false);
    };
    const onConfirm = () => {
        onClose();
        onDelete();
    };

    return (
        <React.Fragment>
            <ControlButton destructive onClick={() => setIsOpen(true)} busy={busy} {...rest}>
                <Icon icon="ph:trash" width="1.3rem" height="1.3rem" className="mr-1" />
                {_t.global.delete()}
            </ControlButton>
            <Dialog.Full
                title={_t.global.delete()}
                description={_t.global.confirmDelete()}
                isOpen={isOpen}
                onClose={onClose}
            >
                <div className="flex flex-wrap items-baseline justify-end gap-2">
                    <Button destructive onClick={onConfirm}>
                        {_t.global.confirm()}
                    </Button>
                    <Button onClick={onClose}>{_t.global.cancel()}</Button>
                </div>
            </Dialog.Full>
        </React.Fragment>
    );
}

function ChangeTrackerButton(props) {
    const { name } = Crud.useRouteParams();
    const { tableName = name, rowKey, onClick, ...rest } = props;
    const openCT = useChangeTrackerHelper(tableName, rowKey);
    if (!tableName) return null;

    return (
        <ControlButton type="ControlButton" title="ChangeTracker" onClick={callAll(openCT, onClick)} {...rest}>
            <img
                className="icon-sm"
                width="20"
                height="20"
                src="https://www.changetracker.it/413d82005450dd3a7e44.png"
            />
            <span className="ml-1 inline lg:hidden">{_t.global.changeTracker()}</span>
        </ControlButton>
    );
}

export function DetailButtons(props) {
    const { save, clone, delete: _delete, add, search, changeTracker, children, slideOver, busy } = props;

    const { isUpdate } = Crud.useRouteParams();

    return (
        <div className="p-[2px] rounded-lg flex gap-2">
            {isNotFalse(save) && <SaveButton {...save} />}
            <Menu busy={busy}>
                {isUpdate && hasProperties(clone) && <CloneButton {...clone} />}
                {isUpdate && isNotFalse(_delete) && <DeleteButton {..._delete} />}
                {isUpdate && isNotFalse(add) && !slideOver && <AddButton {...add} />}
                {isNotFalse(search) && !slideOver && <SearchButton {...search} />}
                {children}
                {isNotFalse(changeTracker) && isUpdate && <ChangeTrackerButton {...changeTracker} />}
            </Menu>
        </div>
    );
}

export function SearchButtons(props) {
    const { add, imp, changeTracker, children } = props;

    return (
        <Menu>
            {imp && <GoToButton {...imp} />}
            {isNotFalse(add) && <AddButton {...add} />}
            {children}
            {isNotFalse(changeTracker) && <ChangeTrackerButton {...changeTracker} />}
        </Menu>
    );
}

const transition = getPopInProps();

const MobileContext = React.createContext();
MobileContext.displayName = 'MobileContext';
const useIsMobile = () => React.useContext(MobileContext) ?? false;

export function Menu(props) {
    const { children, busy } = props;
    return (
        <React.Fragment>
            <div className="hidden lg:contents">{children}</div>
            <div className="relative contents lg:hidden">
                <HUIMenu className={addClasses('relative flex-shrink-0 text-neutral-600')}>
                    {({ open, close }) => (
                        <MobileContext.Provider value={true}>
                            <HUIMenu.Button as={Button} busy={busy} look="primary" size="xs">
                                <span>{_t.interfaccia.azioni()}</span>
                                <ChevronDownIcon className={cn(open && '-scale-y-100', 'icon -my-1 transition-all')} />
                            </HUIMenu.Button>
                            <Transition as={React.Fragment} {...transition}>
                                <HUIMenu.Items
                                    static
                                    className="absolute top-0 right-0 mt-10 mr-2 z-5 flex w-48 origin-top-right flex-col gap-2 rounded-md bg-white p-3 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                                >
                                    {React.Children.map(children, (child, index) => {
                                        if (!child) return null;
                                        return <HUIMenu.Item as={child.type} {...child.props} key={index} />;
                                    })}
                                </HUIMenu.Items>
                            </Transition>
                        </MobileContext.Provider>
                    )}
                </HUIMenu>
            </div>
        </React.Fragment>
    );
}
